// Suggested entries for Quick Add: Dialpad calls, Outlook calendar and sent
// email, and budget hours left (Workload).
//
// Calls and Outlook items are loaded per week when Quick Add opens; budgets
// once. Suggestions the user adds or dismisses are hidden right away (and on
// the server).

// DEPENDENCIES
import { useCallback, useEffect, useRef, useState } from "react";

// FUNCTIONS
import { getAPICall, postAPICall } from "../../config/apiCalls";
import {
	OutlookSignInNeeded,
	getGraphToken,
	readOutlookWeek,
} from "./timeEntryOutlook";

// DATA / HELPERS
import { addDays, fromIso, isoOf } from "./timeEntryUtils";

export const SUGGESTION_SOURCES = {
	email: { label: "Email" },
	call: { label: "Phone call" },
	calendar: { label: "Calendar" },
	workload: { label: "Workload" },
};

// Re-read a week's calls once they're this old
const STALE_MS = 5 * 60 * 1000;

const MATCHED_BY = {
	email: "email",
	mobile: "mobile number",
	phone: "phone number",
};

// "+14255128455" -> "(425) 512-8455"
const fmtPhone = (e164) => {
	const d = String(e164 || "").replace(/\D/g, "");
	const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
	return ten.length === 10
		? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
		: e164 || "";
};

const fmtTime = (ms) =>
	new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const fmtMinutes = (ms) => {
	const mins = ms / 60000;
	return mins < 1 ? `${Math.max(1, Math.round(ms / 1000))} sec` : `${Math.round(mins)} min`;
};

// Call length in hours, rounded up to the next 0.1
const callHours = (ms) => String(Math.max(1, Math.ceil(ms / 360000)) / 10);

// One server call -> a suggestion card
const fromCall = (c) => {
	const who = c.contactName || fmtPhone(c.phone) || "Unknown caller";
	return {
		id: c.id,
		source: "call",
		callId: c.callId,
		phone: c.phone,
		date: isoOf(new Date(c.startedAt)),
		at: c.startedAt,
		hours: callHours(c.durationMs),
		memo: `Phone call with ${who}`,
		title: (c.direction === "outbound" ? "Outgoing call" : "Incoming call") + " · " + who,
		summary: `${fmtTime(c.startedAt)} – ${fmtTime(c.endedAt)} · ${fmtMinutes(c.durationMs)}`,
		detail: [c.contactName && fmtPhone(c.phone), c.contactEmail].filter(Boolean).join(" · "),
		matchLabel: MATCHED_BY[c.matchedBy]
			? `Matched by ${MATCHED_BY[c.matchedBy]} in HubSpot`
			: "",
		projectIds: c.projectIds || [],
		rememberedProjectId: c.rememberedProjectId || null,
	};
};

const OUTLOOK_MATCH = {
	contact: {
		calendar: "Matched by attendee email in HubSpot",
		email: "Matched by recipient email in HubSpot",
	},
	number: "Project number in the title",
	name: "Project name in the title",
};

// "Dana Whitfield, Angie Behrbaum +3"
const fmtPeople = (people) => {
	const names = people.map((p) => p.name || p.email);
	return names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "");
};

// One server calendar event or email conversation -> a suggestion card
const fromOutlook = (o) => {
	const subject = o.subject || "(No subject)";
	const base = {
		id: o.id,
		source: o.source,
		hideKey: o.hideKey,
		date: o.date,
		at: o.start,
		matchLabel: o.by === "contact" ? OUTLOOK_MATCH.contact[o.source] : OUTLOOK_MATCH[o.by] || "",
		// A project name in a title can be a person or a common word, so it's
		// only ever offered, never filled in
		weakMatch: o.by === "name",
		projectIds: o.projectIds || [],
		rememberedProjectId: null,
	};
	if (o.source === "calendar") {
		return {
			...base,
			hours: callHours(o.end - o.start),
			memo: subject,
			title: subject,
			summary: `${fmtTime(o.start)} – ${fmtTime(o.end)} · ${fmtMinutes(o.end - o.start)}`,
			detail: [o.people.length && "With " + fmtPeople(o.people), o.location]
				.filter(Boolean)
				.join(" · "),
		};
	}
	return {
		...base,
		hours: "", // email time is too variable to guess
		memo: `Email re: ${subject}`,
		title: subject,
		summary:
			`${o.count} sent · ${fmtTime(o.start)}` +
			(o.count > 1 && o.end !== o.start ? ` – ${fmtTime(o.end)}` : ""),
		detail: o.people.length ? "To " + fmtPeople(o.people) : "",
	};
};

// Possible projects for a suggestion, best first: the one last used for this
// number, then the user's own (budgeted / managing), then most recently used.
// Only projects the page can log to are kept.
export function rankProjects(s, projectOf) {
	const known = (id) => projectOf(id).name !== "Unknown project";
	const ids = [...new Set([s.rememberedProjectId, ...s.projectIds])].filter(
		(id) => id && known(id),
	);
	return ids.sort((a, b) => {
		if (a === s.rememberedProjectId) return -1;
		if (b === s.rememberedProjectId) return 1;
		const pa = projectOf(a);
		const pb = projectOf(b);
		if (!!pb.yours !== !!pa.yours) return pb.yours ? 1 : -1;
		return String(pb.lastUsed || "").localeCompare(String(pa.lastUsed || ""));
	});
}

// The project to fill in: the remembered one, or the only match (unless the
// match is only a project name in a title)
export const prefillProject = (s, ranked) =>
	s.rememberedProjectId && ranked[0] === s.rememberedProjectId
		? ranked[0]
		: ranked.length === 1 && !s.weakMatch
			? ranked[0]
			: "";

// Workload: budgeted hours left on a phase, offered on every day the user
// hasn't logged to it yet. Hours fill what's left of the day's target.
export const workloadKey = (projectId, date) => projectId + "|" + date;

export function workloadForDay(budgets, dismissed, dayIso, projectHours) {
	return budgets.filter(
		(b) => !dismissed[workloadKey(b.projectId, dayIso)] && projectHours(b.projectId) <= 0,
	);
}

// Hours to fill: the rest of the day's target, at most what's left on the budget
export const workloadHours = (budget, dayTarget, dayHours) => {
	const gap = Math.round(Math.min(budget.remainingHours, dayTarget - dayHours) * 10) / 10;
	return gap > 0 ? String(gap) : "";
};

export function useSuggestions(instance, account, weekKey, active) {
	// weekKey -> { status: loading | ready | error | no-user, items, loadedAt, matchError }
	const [byWeek, setByWeek] = useState({});
	const [hidden, setHidden] = useState({});
	const loading = useRef(new Set());
	// Workload isn't per week: { status, budgets, dismissed: { key: true }, loadedAt }
	const [workload, setWorkload] = useState({ status: "loading", budgets: [], dismissed: {} });
	const workloadBusy = useRef(false);

	const loadWorkload = useCallback(async () => {
		if (workloadBusy.current) return;
		workloadBusy.current = true;
		try {
			const res = await getAPICall(instance, account, "/api/time-entry/suggestions/workload");
			setWorkload({
				status: "ready",
				budgets: res.data.budgets || [],
				dismissed: Object.fromEntries((res.data.dismissed || []).map((k) => [k, true])),
				loadedAt: Date.now(),
			});
		} catch (error) {
			console.error("TimeEntry - load workload - error:", error);
			setWorkload((w) => ({ ...w, status: w.budgets.length ? "ready" : "error" }));
		} finally {
			workloadBusy.current = false;
		}
	}, [instance, account]);

	useEffect(() => {
		if (!active) return;
		if (!workload.loadedAt || Date.now() - workload.loadedAt > STALE_MS) loadWorkload();
	}, [active]); // eslint-disable-line react-hooks/exhaustive-deps

	// Hides a budget for one day only
	const dismissWorkload = useCallback(
		(projectId, dayIso) => {
			setWorkload((w) => ({
				...w,
				dismissed: { ...w.dismissed, [workloadKey(projectId, dayIso)]: true },
			}));
			postAPICall(instance, account, "/api/time-entry/suggestions/hide", {
				source: "workload",
				kind: "dismissed",
				projectId,
				date: dayIso,
			}).catch((error) => console.error("TimeEntry - dismiss workload - error:", error));
		},
		[instance, account],
	);

	const load = useCallback(
		async (key) => {
			if (loading.current.has(key)) return;
			loading.current.add(key);
			setByWeek((w) => ({
				...w,
				[key]: { items: [], ...w[key], status: w[key]?.items?.length ? "ready" : "loading" },
			}));
			try {
				const from = fromIso(key).getTime();
				const to = addDays(fromIso(key), 7).getTime();
				const res = await getAPICall(instance, account, "/api/time-entry/suggestions", { from, to });
				const status = res.data.calls?.status === "no-user" ? "no-user" : "ready";
				setByWeek((w) => ({
					...w,
					[key]: {
						status,
						items: (res.data.suggestions || []).map(fromCall),
						loadedAt: Date.now(),
						matchError: res.data.calls?.matchError || null,
					},
				}));
			} catch (error) {
				console.error("TimeEntry - load suggestions - error:", error);
				setByWeek((w) => ({
					...w,
					[key]: {
						items: [],
						...w[key],
						status: "error",
						error: error?.response?.data?.error || "Couldn't load your Dialpad calls.",
					},
				}));
			} finally {
				loading.current.delete(key);
			}
		},
		[instance, account],
	);

	useEffect(() => {
		if (!active) return;
		const wk = byWeek[weekKey];
		if (!wk || (wk.status === "ready" && Date.now() - wk.loadedAt > STALE_MS)) load(weekKey);
	}, [active, weekKey]); // eslint-disable-line react-hooks/exhaustive-deps

	// Outlook, per week: { status: loading | ready | signin | error, items, loadedAt, matchError, error }
	const [outlookByWeek, setOutlookByWeek] = useState({});
	const outlookLoading = useRef(new Set());

	// interactive: show Microsoft's sign-in popup if the session has expired
	const loadOutlook = useCallback(
		async (key, interactive = false) => {
			if (outlookLoading.current.has(key)) return;
			outlookLoading.current.add(key);
			const set = (patch) =>
				setOutlookByWeek((w) => ({ ...w, [key]: { items: [], ...w[key], ...patch } }));
			set({ status: "loading" });
			try {
				const token = await getGraphToken(instance, account, interactive);
				const from = fromIso(key);
				const { events, threads } = await readOutlookWeek(
					token,
					from,
					addDays(from, 7),
					account?.username,
				);
				const res = await postAPICall(instance, account, "/api/time-entry/suggestions/outlook", {
					events,
					threads,
				});
				set({
					status: "ready",
					items: (res.data.suggestions || []).map(fromOutlook),
					loadedAt: Date.now(),
					matchError: res.data.matchError || null,
				});
			} catch (error) {
				if (error instanceof OutlookSignInNeeded) {
					set({ status: "signin" });
				} else {
					console.error("TimeEntry - load Outlook - error:", error);
					set({
						status: "error",
						error:
							error?.response?.data?.error ||
							(interactive
								? "Couldn't sign in to Microsoft for your calendar and email."
								: "Couldn't load your calendar and email."),
					});
				}
			} finally {
				outlookLoading.current.delete(key);
			}
		},
		[instance, account],
	);

	useEffect(() => {
		if (!active) return;
		const wk = outlookByWeek[weekKey];
		if (!wk || (wk.status === "ready" && Date.now() - wk.loadedAt > STALE_MS)) loadOutlook(weekKey);
	}, [active, weekKey]); // eslint-disable-line react-hooks/exhaustive-deps

	// kind: dismissed | used (a call's "used" also remembers the project for its number)
	const hide = useCallback(
		(s, kind, projectId = null) => {
			setHidden((h) => ({ ...h, [s.id]: true }));
			postAPICall(instance, account, "/api/time-entry/suggestions/hide", {
				source: s.source,
				kind,
				callId: s.callId,
				phone: s.phone,
				key: s.hideKey,
				projectId,
			}).catch((error) => console.error("TimeEntry - hide suggestion - error:", error));
		},
		[instance, account],
	);

	const wk = byWeek[weekKey] || { status: "loading", items: [] };
	const ol = outlookByWeek[weekKey] || { status: "loading", items: [] };
	const visible = (items) => items.filter((s) => !hidden[s.id]);
	return {
		status: wk.status,
		error: wk.error || null,
		matchError: wk.matchError || ol.matchError || null,
		suggestions: visible(wk.items),
		reload: () => load(weekKey),
		outlook: {
			status: ol.status,
			error: ol.error || null,
			items: visible(ol.items),
		},
		reloadOutlook: () => loadOutlook(weekKey),
		connectOutlook: () => loadOutlook(weekKey, true),
		hide,
		workload,
		reloadWorkload: loadWorkload,
		dismissWorkload,
	};
}
